import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { 
  ChevronLeft, ChevronDown, ChevronUp, Edit2, X, 
  Calendar as CalendarIcon, Receipt, DollarSign, Package,
  Search, Filter, ArrowUpDown, Download, History,
  TrendingUp, BarChart3, AlertTriangle, CheckCircle2, XCircle,
  Eye, Plus, Minus, ShoppingBag, Zap, Globe, Settings,
  PieChart, Tag, Percent, Lock, Crown, Trash2
} from 'lucide-react';
import { CardSkeleton } from '../common/Skeleton';
import EmptyState from '../common/EmptyState';
import ConfirmModal from '../common/ConfirmModal';
import InventoryForm from './InventoryForm';
import InventoryDetailModal from './InventoryDetailModal';
import toast from 'react-hot-toast';
import { 
  SalesModeGate, 
  AddInventoryGate, 
  ExportGate,
  UpgradeBadge,
  UpgradeModal
} from '../subscription/FeatureGates';
import { useFeatureAccess } from '../../contexts/SubscriptionContext';

// Items that have size variants
const SIZED_ITEMS = ['T-Shirt', 'Long Sleeve', 'Hoodie', 'Hat', 'Tank Top', 'Crewneck'];

// Currency configuration
const CURRENCIES = {
  USD: { symbol: '$', name: 'US Dollar' },
  EUR: { symbol: '€', name: 'Euro' },
  GBP: { symbol: '£', name: 'British Pound' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar' },
  AUD: { symbol: 'A$', name: 'Australian Dollar' },
  JPY: { symbol: '¥', name: 'Japanese Yen' },
  CHF: { symbol: 'CHF', name: 'Swiss Franc' },
  SEK: { symbol: 'kr', name: 'Swedish Krona' },
  NOK: { symbol: 'kr', name: 'Norwegian Krone' },
  DKK: { symbol: 'kr', name: 'Danish Krone' },
  MXN: { symbol: '$', name: 'Mexican Peso' },
  BRL: { symbol: 'R$', name: 'Brazilian Real' },
  NZD: { symbol: 'NZ$', name: 'New Zealand Dollar' },
  PLN: { symbol: 'zł', name: 'Polish Zloty' },
  CZK: { symbol: 'Kč', name: 'Czech Koruna' }
};

const Inventory = ({
  inventory = [],
  shows = {},
  onNavigate,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onRecordSale,
  loading,
  isAdmin = true,
  currency: savedCurrency,
  onCurrencyChange,
  onUpdateShow
}) => {
  // Item types - expanded list
  const itemTypes = ['T-Shirt', 'Long Sleeve', 'Hoodie', 'Crewneck', 'Tank Top', 'Hat', 'Vinyl', 'CD', 'Cassette', 'Poster', 'Stickers', 'Patch', 'Pin', 'Tote Bag', 'Flag', 'Setlist', 'Other'];
  const sizes = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', 'N/A'];

  // Subscription check for sales mode
  const hasSalesModeAccess = useFeatureAccess('salesMode');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // State
  const [editingItem, setEditingItem] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [changeLog, setChangeLog] = useState([]);
  const [showChangeLog, setShowChangeLog] = useState(false);
  const [deletedItems, setDeletedItems] = useState([]);
  const [viewingItem, setViewingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [showStats, setShowStats] = useState(false);
  const [showSalesHistoryPanel, setShowSalesHistoryPanel] = useState(false);
  
  // Sales Mode & Currency
  const [salesMode, setSalesMode] = useState(false);
  const [currency, setCurrency] = useState(savedCurrency || 'USD');
  const [showCurrencyPicker, setShowCurrencyPicker] = useState(false);
  
  // Show integration state
  const [showPicker, setShowPicker] = useState(false);
  const [selectedShow, setSelectedShow] = useState(null);
  const [sessionSales, setSessionSales] = useState([]);
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  
  // Clear history modal state
  const [clearHistoryModal, setClearHistoryModal] = useState({ isOpen: false, show: null });
  const [skipClearHistoryConfirm, setSkipClearHistoryConfirm] = useState(() => {
    return localStorage.getItem('boply_skip_clear_history_confirm') === 'true';
  });
  
  // Sale modal state
  const [saleModal, setSaleModal] = useState({
    isOpen: false,
    item: null,
    size: null,
    quantity: 1
  });
  
  const formRef = useRef(null);
  const currencySymbol = CURRENCIES[currency]?.symbol || '$';

  // Format currency helper
  const formatCurrency = useCallback((amount) => {
    const num = parseFloat(amount) || 0;
    if (currency === 'JPY') {
      return `${currencySymbol}${Math.round(num)}`;
    }
    return `${currencySymbol}${num.toFixed(2)}`;
  }, [currency, currencySymbol]);

  // Handle clearing sales history
  const handleClearHistoryClick = useCallback((show) => {
    if (skipClearHistoryConfirm) {
      onUpdateShow(show.dateKey, show.id, { merchSales: [] });
      toast.success('Sales history cleared');
    } else {
      setClearHistoryModal({ isOpen: true, show });
    }
  }, [skipClearHistoryConfirm, onUpdateShow]);

  const handleConfirmClearHistory = useCallback((dontShowAgain) => {
    const { show } = clearHistoryModal;
    if (show) {
      onUpdateShow(show.dateKey, show.id, { merchSales: [] });
      toast.success('Sales history cleared');
    }
    
    if (dontShowAgain) {
      localStorage.setItem('boply_skip_clear_history_confirm', 'true');
      setSkipClearHistoryConfirm(true);
    }
    
    setClearHistoryModal({ isOpen: false, show: null });
  }, [clearHistoryModal, onUpdateShow]);

  // Available shows for show picker (7 days ago to 30 days ahead)
  const availableShows = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const showList = [];
    
    Object.entries(shows).forEach(([dateKey, dayShows]) => {
      const [year, month, day] = dateKey.split('-').map(Number);
      const showDate = new Date(year, month - 1, day);
      const diffTime = today - showDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // if (diffDays <= 7 && diffDays >= -30) {
      // TEMPORARY: Include ALL shows for screenshots
if (true) {  // Was: diffDays <= 7 && diffDays >= -30
        dayShows.forEach(show => {
          if (show.eventType !== 'personal' && show.eventType !== 'other') {
            showList.push({
              ...show,
              dateKey,
              date: showDate,
              isToday: diffDays === 0,
              isPast: diffDays > 0,
              daysAway: -diffDays
            });
          }
        });
      }
    });
    
    return showList.sort((a, b) => Math.abs(a.daysAway) - Math.abs(b.daysAway));
  }, [shows]);

  // Session totals calculation
  const sessionTotals = useMemo(() => {
    const itemsSold = sessionSales.reduce((sum, sale) => sum + sale.quantity, 0);
    const revenue = sessionSales.reduce((sum, sale) => sum + (sale.quantity * sale.price), 0);
    return { itemsSold, revenue };
  }, [sessionSales]);

  // Get shows with merch sales from the shows object
  const showsWithMerchSales = useMemo(() => {
    const result = [];
    
    Object.entries(shows).forEach(([dateKey, dayShows]) => {
      if (!Array.isArray(dayShows)) return;
      
      dayShows.forEach(show => {
        const hasMerchSales = show.merchSales && show.merchSales.length > 0;
        
        if (hasMerchSales) {
          result.push({
            ...show,
            dateKey,
            totalRevenue: parseFloat(show.cashMerch || 0),
            totalItems: show.merchSales.reduce((sum, sale) => sum + (sale.quantity || 0), 0)
          });
        }
      });
    });
    
    return result.sort((a, b) => new Date(b.dateKey) - new Date(a.dateKey));
  }, [shows]);

  // Handle currency change
  const handleCurrencyChange = useCallback((newCurrency) => {
    setCurrency(newCurrency);
    setShowCurrencyPicker(false);
    onCurrencyChange?.(newCurrency);
    toast.success(`Currency changed to ${CURRENCIES[newCurrency].name}`);
  }, [onCurrencyChange]);

  // Helper functions
  const parseSizes = useCallback((item) => {
    if (!item) return null;
    if (Array.isArray(item.sizes)) return item.sizes;
    if (item.size && typeof item.size === 'string' && item.size.startsWith('[')) {
      try { return JSON.parse(item.size); } catch { return null; }
    }
    return null;
  }, []);

  const getItemTotalQuantity = useCallback((item) => {
    if (!item || typeof item !== 'object') return 0;
    const sizes = parseSizes(item);
    if (sizes && sizes.length > 0) {
      return sizes.reduce((sum, s) => sum + (parseInt(s.quantity) || 0), 0);
    }
    return parseInt(item.quantity) || 0;
  }, [parseSizes]);

  const itemHasSizes = useCallback((item) => {
    if (!item) return false;
    const sizes = parseSizes(item);
    return sizes && sizes.length > 0;
  }, [parseSizes]);
  
  const getItemSizes = useCallback((item) => parseSizes(item) || [], [parseSizes]);

  const getItemDisplayName = useCallback((item) => {
    if (!item) return 'Unknown';
    if (item.name && item.name.trim()) return item.name;
    return item.item || 'Unknown';
  }, []);

  // Stats calculation
  const inventoryStats = useMemo(() => {
    const stats = {
      totalValue: 0,
      totalCost: 0,
      totalItems: 0,
      totalProfit: 0,
      lowStock: [], 
      outOfStock: [], 
      inStock: [], 
      uniqueProducts: new Set(),
      byCategory: {}
    };
    
    if (!inventory || !Array.isArray(inventory)) return { ...stats, avgMargin: 0, profitMargin: 0 };

    inventory.forEach(item => {
      if (!item || typeof item !== 'object') return;
      
      const sellPrice = parseFloat(item.sellPrice || 0);
      const cost = parseFloat(item.cost || item.msrp || 0);
      const reorderPoint = parseInt(item.reorderPoint || 5);
      const category = item.item || 'Other';
      
      stats.uniqueProducts.add(getItemDisplayName(item));
      
      if (!stats.byCategory[category]) {
        stats.byCategory[category] = {
          totalValue: 0,
          totalCost: 0,
          totalItems: 0,
          profit: 0
        };
      }

      if (itemHasSizes(item)) {
        const sizes = getItemSizes(item);
        let itemTotalQty = 0;
        let hasOutOfStock = false;
        let hasLowStock = false;

        sizes.forEach(sizeData => {
          const qty = parseInt(sizeData.quantity) || 0;
          itemTotalQty += qty;
          
          const itemValue = qty * sellPrice;
          const itemCost = qty * cost;
          const itemProfit = qty * (sellPrice - cost);
          
          stats.totalValue += itemValue;
          stats.totalCost += itemCost;
          stats.totalItems += qty;
          stats.totalProfit += itemProfit;
          
          stats.byCategory[category].totalValue += itemValue;
          stats.byCategory[category].totalCost += itemCost;
          stats.byCategory[category].totalItems += qty;
          stats.byCategory[category].profit += itemProfit;
          
          if (qty === 0) hasOutOfStock = true;
          else if (qty <= reorderPoint) hasLowStock = true;
        });

        if (itemTotalQty === 0) stats.outOfStock.push(item);
        else if (hasOutOfStock || hasLowStock) stats.lowStock.push(item);
        else stats.inStock.push(item);
      } else {
        const qty = parseInt(item.quantity || 0);
        
        const itemValue = qty * sellPrice;
        const itemCost = qty * cost;
        const itemProfit = qty * (sellPrice - cost);
        
        stats.totalValue += itemValue;
        stats.totalCost += itemCost;
        stats.totalItems += qty;
        stats.totalProfit += itemProfit;
        
        stats.byCategory[category].totalValue += itemValue;
        stats.byCategory[category].totalCost += itemCost;
        stats.byCategory[category].totalItems += qty;
        stats.byCategory[category].profit += itemProfit;
        
        if (qty === 0) stats.outOfStock.push(item);
        else if (qty <= reorderPoint) stats.lowStock.push(item);
        else stats.inStock.push(item);
      }
    });

    stats.avgMargin = stats.totalValue > 0 ? ((stats.totalProfit / stats.totalValue) * 100).toFixed(1) : 0;
    stats.profitMargin = stats.totalCost > 0 ? ((stats.totalProfit / stats.totalCost) * 100).toFixed(1) : 0;
    
    return stats;
  }, [inventory, itemHasSizes, getItemSizes, getItemDisplayName]);

  // Filter and sort
  const filteredAndSortedInventory = useMemo(() => {
    if (!inventory || !Array.isArray(inventory)) return [];
    
    let result = [...inventory].filter(item => item && typeof item === 'object' && item.item);

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => {
        const displayName = getItemDisplayName(item).toLowerCase();
        const itemType = (item.item || '').toLowerCase();
        const itemSku = (item.sku || '').toLowerCase();
        const itemNotes = (item.notes || '').toLowerCase();
        return displayName.includes(term) || itemType.includes(term) || itemSku.includes(term) || itemNotes.includes(term);
      });
    }

    if (filterStatus !== 'all') {
      result = result.filter(item => {
        const reorderPoint = parseInt(item.reorderPoint || 5);
        const totalQty = getItemTotalQuantity(item);
        
        if (itemHasSizes(item)) {
          const sizes = getItemSizes(item);
          const hasOutOfStock = sizes.some(s => parseInt(s.quantity) === 0);
          const hasLowStock = sizes.some(s => { const q = parseInt(s.quantity); return q > 0 && q <= reorderPoint; });
          
          if (filterStatus === 'out') return totalQty === 0 || hasOutOfStock;
          if (filterStatus === 'low') return hasLowStock;
          if (filterStatus === 'in') return !hasOutOfStock && !hasLowStock;
        } else {
          if (filterStatus === 'out') return totalQty === 0;
          if (filterStatus === 'low') return totalQty > 0 && totalQty <= reorderPoint;
          if (filterStatus === 'in') return totalQty > reorderPoint;
        }
        return true;
      });
    }

    result.sort((a, b) => {
      let cmp = 0;
      const nameA = getItemDisplayName(a);
      const nameB = getItemDisplayName(b);
      const qtyA = getItemTotalQuantity(a);
      const qtyB = getItemTotalQuantity(b);
      
      switch (sortBy) {
        case 'name': cmp = nameA.localeCompare(nameB); break;
        case 'quantity': cmp = qtyA - qtyB; break;
        case 'value': cmp = (qtyA * parseFloat(a.sellPrice)) - (qtyB * parseFloat(b.sellPrice)); break;
        default: cmp = 0;
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [inventory, searchTerm, filterStatus, sortBy, sortOrder, itemHasSizes, getItemTotalQuantity, getItemSizes, getItemDisplayName]);

  // Group by type
  const inventoryByType = useMemo(() => {
    return itemTypes.map(type => ({
      type,
      items: filteredAndSortedInventory.filter(item => item && String(item.item || '') === type),
      totalQuantity: filteredAndSortedInventory.filter(item => item && String(item.item || '') === type).reduce((sum, item) => sum + getItemTotalQuantity(item), 0),
      totalValue: filteredAndSortedInventory.filter(item => item && String(item.item || '') === type).reduce((sum, item) => sum + (getItemTotalQuantity(item) * parseFloat(item.sellPrice || 0)), 0)
    })).filter(group => group.items.length > 0);
  }, [filteredAndSortedInventory, itemTypes, getItemTotalQuantity]);

  // Stock status helper
  const getStockStatus = useCallback((item) => {
    if (!item) return { label: 'Unknown', bgClass: 'bg-gray-100 text-gray-700' };
    const reorderPoint = parseInt(item.reorderPoint || 5);
    
    if (itemHasSizes(item)) {
      const sizes = getItemSizes(item);
      const totalQty = getItemTotalQuantity(item);
      const outCount = sizes.filter(s => parseInt(s.quantity) === 0).length;
      const lowCount = sizes.filter(s => { const q = parseInt(s.quantity); return q > 0 && q <= reorderPoint; }).length;

      if (totalQty === 0) return { label: 'Out', icon: <XCircle size={12} />, bgClass: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' };
      if (outCount > 0) return { label: `${outCount} out`, icon: <AlertTriangle size={12} />, bgClass: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' };
      if (lowCount > 0) return { label: `${lowCount} low`, icon: <AlertTriangle size={12} />, bgClass: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' };
      return { label: 'OK', icon: <CheckCircle2 size={12} />, bgClass: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' };
    } else {
      const qty = parseInt(item.quantity);
      if (qty === 0) return { label: 'Out', icon: <XCircle size={12} />, bgClass: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' };
      if (qty <= reorderPoint) return { label: 'Low', icon: <AlertTriangle size={12} />, bgClass: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' };
      return { label: 'OK', icon: <CheckCircle2 size={12} />, bgClass: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' };
    }
  }, [itemHasSizes, getItemTotalQuantity, getItemSizes]);

  // Toggle expansion
  const toggleItemExpanded = useCallback((itemId) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  // Event handlers
  const handleAddItem = useCallback((formData) => {
    if (formData.hasSizes && formData.sizes?.length > 0) {
      onAddItem(formData.item, JSON.stringify(formData.sizes), (formData.sizes || []).reduce((sum, s) => sum + (parseInt(s.quantity) || 0), 0).toString(), formData.cost, formData.sellPrice, '', formData.reorderPoint, formData.imageUrl, formData.notes, true, formData.name);
    } else {
      onAddItem(formData.item, formData.size || 'N/A', formData.quantity, formData.cost, formData.sellPrice, formData.sku || '', formData.reorderPoint, formData.imageUrl, formData.notes, false, formData.name);
    }
    setShowAddForm(false);
    toast.success('Item added');
  }, [onAddItem]);

  const handleUpdateItem = useCallback((formData) => {
    if (formData.hasSizes && formData.sizes?.length > 0) {
      onUpdateItem(editingItem.id, formData.item, JSON.stringify(formData.sizes), (formData.sizes || []).reduce((sum, s) => sum + (parseInt(s.quantity) || 0), 0).toString(), formData.cost, formData.sellPrice, '', formData.reorderPoint, formData.imageUrl, formData.notes, true, formData.name);
    } else {
      onUpdateItem(editingItem.id, formData.item, formData.size || 'N/A', formData.quantity, formData.cost, formData.sellPrice, formData.sku || '', formData.reorderPoint, formData.imageUrl, formData.notes, false, formData.name);
    }
    setEditingItem(null);
    setShowAddForm(false);
    toast.success('Item updated');
  }, [editingItem, onUpdateItem]);

  const handleQuickQuantityChange = useCallback((item, newQty) => {
    onUpdateItem(item.id, item.item, item.size || 'N/A', newQty.toString(), item.cost || item.msrp, item.sellPrice, item.sku || '', item.reorderPoint, item.imageUrl, item.notes, false, item.name);
    const diff = newQty - parseInt(item.quantity);
    toast.success(`${getItemDisplayName(item)}: ${diff > 0 ? '+' : ''}${diff}`);
  }, [onUpdateItem, getItemDisplayName]);

  const handleSizeQuantityChange = useCallback((item, sizeToUpdate, newQty) => {
    const sizes = getItemSizes(item);
    const updatedSizes = sizes.map(s => s.size === sizeToUpdate ? { ...s, quantity: newQty } : s);
    onUpdateItem(item.id, item.item, JSON.stringify(updatedSizes), updatedSizes.reduce((sum, s) => sum + (parseInt(s.quantity) || 0), 0).toString(), item.cost || item.msrp, item.sellPrice, '', item.reorderPoint, item.imageUrl, item.notes, true, item.name);
    const oldQty = sizes.find(s => s.size === sizeToUpdate)?.quantity || 0;
    const diff = newQty - parseInt(oldQty);
    toast.success(`${getItemDisplayName(item)} (${sizeToUpdate}): ${diff > 0 ? '+' : ''}${diff}`);
  }, [onUpdateItem, getItemSizes, getItemDisplayName]);

  // SALES MODE: Quick sell
  const handleQuickSell = useCallback((item, size = null, qty = 1) => {
    if (size) {
      const sizes = getItemSizes(item);
      const sizeData = sizes.find(s => s.size === size);
      if (!sizeData || parseInt(sizeData.quantity) < qty) {
        toast.error('Not enough stock!');
        return;
      }
      const newQty = Math.max(0, parseInt(sizeData.quantity) - qty);
      handleSizeQuantityChange(item, size, newQty);
    } else {
      if (parseInt(item.quantity) < qty) {
        toast.error('Not enough stock!');
        return;
      }
      const newQty = Math.max(0, parseInt(item.quantity) - qty);
      handleQuickQuantityChange(item, newQty);
    }
    
    const saleRecord = {
      id: Date.now().toString(),
      itemId: item.id,
      itemName: getItemDisplayName(item),
      category: item.item,
      size: size || 'N/A',
      quantity: qty,
      price: parseFloat(item.sellPrice),
      timestamp: new Date().toISOString()
    };
    setSessionSales(prev => [...prev, saleRecord]);
    
    const sizeText = size ? ` (${size})` : '';
    toast.success(`Sold ${qty}x ${getItemDisplayName(item)}${sizeText} - ${formatCurrency(qty * parseFloat(item.sellPrice))}`, { duration: 2000 });
  }, [getItemSizes, handleSizeQuantityChange, handleQuickQuantityChange, getItemDisplayName, formatCurrency]);

  // Handle entering sales mode
  const handleEnterSalesMode = useCallback(() => {
    if (!hasSalesModeAccess) {
      setShowUpgradeModal(true);
      return;
    }
    
    if (availableShows.length > 0) {
      setShowPicker(true);
    } else {
      setSalesMode(true);
    }
  }, [availableShows, hasSalesModeAccess]);

  const handleSelectShow = useCallback((show) => {
    setSelectedShow(show);
    setShowPicker(false);
    setSessionSales([]);
    setSalesMode(true);
  }, []);

  const handleSkipShowSelection = useCallback(() => {
    setSelectedShow(null);
    setShowPicker(false);
    setSessionSales([]);
    setSalesMode(true);
  }, []);

  const handleExitSalesMode = useCallback(() => {
    if (sessionSales.length > 0) {
      setShowSessionSummary(true);
    } else {
      setSalesMode(false);
      setSelectedShow(null);
      setSessionSales([]);
    }
  }, [sessionSales]);

  const handleSaveSession = useCallback(() => {
    if (selectedShow && sessionSales.length > 0 && onUpdateShow) {
      const latestShowData = shows[selectedShow.dateKey]?.find(s => s.id === selectedShow.id);
      const existingMerchSales = latestShowData?.merchSales || selectedShow.merchSales || [];
      
      const newMerchSales = sessionSales.map(sale => ({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        itemName: sale.itemName,
        category: sale.category,
        size: sale.size,
        quantity: sale.quantity,
        price: sale.price,
        total: sale.quantity * sale.price,
        timestamp: sale.timestamp
      }));
      
      const currentCashMerch = parseFloat(latestShowData?.cashMerch || selectedShow.cashMerch || 0);
      const newCashMerch = currentCashMerch + sessionTotals.revenue;
      
      onUpdateShow(selectedShow.dateKey, selectedShow.id, {
        merchSales: [...existingMerchSales, ...newMerchSales],
        cashMerch: newCashMerch.toString()
      });
      
      toast.success(`Saved ${sessionTotals.itemsSold} items (${formatCurrency(sessionTotals.revenue)}) to ${selectedShow.title}`);
    }
    
    setShowSessionSummary(false);
    setSalesMode(false);
    setSelectedShow(null);
    setSessionSales([]);
  }, [selectedShow, sessionSales, sessionTotals, onUpdateShow, formatCurrency, shows]);

  const handleDiscardSession = useCallback(() => {
    setShowSessionSummary(false);
    setSalesMode(false);
    setSelectedShow(null);
    setSessionSales([]);
  }, []);

  const handleDeleteItem = useCallback((item) => {
    if (!item) return;
    setDeletedItems(prev => [...prev, item]);
    onDeleteItem?.(item.id);
    toast.success(`Deleted ${getItemDisplayName(item)}`);
    setConfirmDelete(null);
  }, [onDeleteItem, getItemDisplayName]);

  const handleEditItem = useCallback((item) => {
    setEditingItem(item);
    setShowAddForm(true);
    setSalesMode(false);
  }, []);

  const handleViewItem = useCallback((item) => setViewingItem(item), []);
  const handleCancelEdit = useCallback(() => { setEditingItem(null); setShowAddForm(false); }, []);

  // CSV Export
  const exportToCSV = useCallback(() => {
    const headers = ['Name', 'Category', 'Size', 'SKU', 'Qty', 'Cost', 'Price', 'Value'];
    const rows = [];
    inventory.forEach(item => {
      const displayName = getItemDisplayName(item);
      if (itemHasSizes(item)) {
        getItemSizes(item).forEach(s => {
          rows.push([displayName, item.item, s.size, s.sku || '', s.quantity, item.cost || item.msrp, item.sellPrice, (parseInt(s.quantity) * parseFloat(item.sellPrice)).toFixed(2)]);
        });
      } else {
        rows.push([displayName, item.item, item.size || 'N/A', item.sku || '', item.quantity, item.cost || item.msrp, item.sellPrice, (parseInt(item.quantity) * parseFloat(item.sellPrice)).toFixed(2)]);
      }
    });
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `inventory_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Exported!');
  }, [inventory, itemHasSizes, getItemSizes, getItemDisplayName]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
          <CardSkeleton /><CardSkeleton /><CardSkeleton />
        </div>
      </div>
    );
  }

  // ==================== SALES MODE UI ====================
  if (salesMode) {
    return (
      <div className="fixed inset-0 top-16 md:top-0 bg-gray-900 text-white overflow-hidden flex flex-col z-50">
        {/* Sales Mode Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-purple-600 to-pink-600 shadow-lg py-4 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Top row - SALES MODE title + show info */}
            <div className="text-center mb-3">
              <span className="text-2xl md:text-3xl font-bold tracking-wide">SALES MODE</span>
              {selectedShow && (
                <p className="text-sm md:text-base opacity-90 mt-1">
                  📍 {selectedShow.title} • {new Date(selectedShow.date).toLocaleDateString()}
                </p>
              )}
            </div>
            
            {/* Session stats row */}
            {sessionSales.length > 0 && (
              <div className="flex justify-center gap-4 mb-4 text-sm md:text-base">
                <span className="bg-white/20 px-4 py-1.5 rounded-full">
                  🛒 {sessionTotals.itemsSold} items
                </span>
                <span className="bg-green-500/30 px-4 py-1.5 rounded-full">
                  💰 {formatCurrency(sessionTotals.revenue)}
                </span>
              </div>
            )}
            
            {/* Bottom row - back button, currency, stock */}
            <div className="flex items-center justify-between max-w-md mx-auto">
              <button onClick={handleExitSalesMode} className="p-2 md:p-3 bg-white/20 rounded-full hover:bg-white/30">
                <ChevronLeft size={24} />
              </button>
              <button onClick={() => setShowCurrencyPicker(true)} className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30">
                <Globe size={18} />
                <span className="font-bold text-lg">{currency}</span>
              </button>
              <div className="text-center">
                <p className="text-xs opacity-80">Stock</p>
                <p className="text-lg font-bold">{inventoryStats.totalItems}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search - fixed height */}
        <div className="flex-shrink-0 p-4 md:py-6">
          <div className="max-w-3xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={24} />
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-14 pr-4 py-4 bg-gray-800 border-2 border-gray-700 rounded-xl text-xl focus:border-purple-500 focus:outline-none"
              autoFocus
            />
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">
          {/* Sales Grid - BIG BUTTONS */}
          <div className="max-w-7xl mx-auto p-4 md:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 pb-8">
            {filteredAndSortedInventory.map(item => {
              const displayName = getItemDisplayName(item);
              const hasSizes = itemHasSizes(item);
              const totalQty = getItemTotalQuantity(item);
              const isExpanded = expandedItems.has(item.id);

              return (
                <div key={item.id} className="bg-gray-800 rounded-2xl overflow-hidden border-2 border-gray-700 hover:border-purple-500 transition-colors">
                  {/* Item Header */}
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={displayName} className="w-20 h-20 rounded-xl object-cover" />
                      ) : (
                        <div className="w-20 h-20 rounded-xl bg-gray-700 flex items-center justify-center">
                          <Package size={32} className="text-gray-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg text-white truncate">{displayName}</h3>
                        <p className="text-gray-400 text-sm">{item.item}</p>
                        <p className="text-2xl font-bold text-green-400 mt-1">{formatCurrency(item.sellPrice)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Sell Area */}
                  {hasSizes ? (
                    <>
                      <button
                        onClick={() => toggleItemExpanded(item.id)}
                        className="w-full py-3 bg-gray-700 text-white font-semibold flex items-center justify-center gap-2 hover:bg-gray-600"
                      >
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        {totalQty} total • Tap for sizes
                      </button>
                      
                      {isExpanded && (
                        <div className="p-3 bg-gray-900 grid grid-cols-2 gap-2">
                          {getItemSizes(item).map(sizeData => {
                            const qty = parseInt(sizeData.quantity) || 0;
                            const isOut = qty === 0;
                            return (
                              <button
                                key={sizeData.size}
                                onClick={() => !isOut && handleQuickSell(item, sizeData.size, 1)}
                                disabled={isOut}
                                className={`p-4 rounded-xl font-bold text-center transition-all active:scale-95 ${
                                  isOut
                                    ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                                    : 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg'
                                }`}
                              >
                                <span className="text-2xl block">{sizeData.size}</span>
                                <span className="text-sm opacity-80">{qty} left</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="p-3 bg-gray-900">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 flex-1">Stock: <span className="font-bold text-white">{item.quantity}</span></span>
                        <button
                          onClick={() => handleQuickSell(item, null, 1)}
                          disabled={parseInt(item.quantity) === 0}
                          className={`flex-1 py-4 rounded-xl font-bold text-xl transition-all active:scale-95 ${
                            parseInt(item.quantity) === 0
                              ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                              : 'bg-green-600 hover:bg-green-500 text-white shadow-lg'
                          }`}
                        >
                          SELL
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {filteredAndSortedInventory.length === 0 && (
            <div className="text-center py-20 text-gray-500">
              <Package size={64} className="mx-auto mb-4 opacity-50" />
              <p className="text-xl">No items found</p>
            </div>
          )}

          {/* Mobile Exit Button - At bottom of scroll */}
          <div className="md:hidden p-4 pb-8">
            <button
              onClick={handleExitSalesMode}
              className="w-full py-4 bg-gradient-to-r from-purple-600/80 to-pink-600/80 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-bold flex items-center justify-center gap-2"
            >
              <X size={20} />
              Exit Sales Mode
              {sessionSales.length > 0 && (
                <span className="bg-white/30 text-white text-sm px-2 py-0.5 rounded-full ml-2">
                  {sessionTotals.itemsSold} sold
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Currency Picker Modal */}
        {showCurrencyPicker && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-xl font-bold">Select Currency</h3>
                <button onClick={() => setShowCurrencyPicker(false)} className="p-2 hover:bg-gray-700 rounded-lg">
                  <X size={24} />
                </button>
              </div>
              <div className="p-2 max-h-[60vh] overflow-y-auto">
                {Object.entries(CURRENCIES).map(([code, { symbol, name }]) => (
                  <button
                    key={code}
                    onClick={() => handleCurrencyChange(code)}
                    className={`w-full p-4 text-left rounded-xl mb-1 flex items-center justify-between ${
                      currency === code ? 'bg-purple-600' : 'hover:bg-gray-700'
                    }`}
                  >
                    <span>
                      <span className="font-bold text-lg mr-2">{symbol}</span>
                      <span className="text-gray-300">{name}</span>
                    </span>
                    <span className="font-mono text-gray-400">{code}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Session Summary Modal */}
        {showSessionSummary && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b border-gray-700">
                <h3 className="text-xl font-bold text-center">Session Summary</h3>
                {selectedShow && (
                  <p className="text-center text-gray-400 text-sm mt-1">
                    {selectedShow.title} • {new Date(selectedShow.date).toLocaleDateString()}
                  </p>
                )}
              </div>
              
              <div className="p-4">
                {/* Totals */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-purple-600/20 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-purple-400">{sessionTotals.itemsSold}</p>
                    <p className="text-sm text-gray-400">Items Sold</p>
                  </div>
                  <div className="bg-green-600/20 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-green-400">{formatCurrency(sessionTotals.revenue)}</p>
                    <p className="text-sm text-gray-400">Revenue</p>
                  </div>
                </div>
                
                {/* Item breakdown */}
                <div className="max-h-48 overflow-y-auto mb-4">
                  {Object.entries(
                    sessionSales.reduce((acc, sale) => {
                      const key = `${sale.itemName}-${sale.size}`;
                      if (!acc[key]) {
                        acc[key] = { ...sale, quantity: 0, total: 0 };
                      }
                      acc[key].quantity += sale.quantity;
                      acc[key].total += sale.quantity * sale.price;
                      return acc;
                    }, {})
                  ).map(([key, item]) => (
                    <div key={key} className="flex justify-between py-2 border-b border-gray-700">
                      <span>
                        {item.itemName} {item.size !== 'N/A' && `(${item.size})`}
                        <span className="text-gray-500 ml-2">×{item.quantity}</span>
                      </span>
                      <span className="text-green-400">{formatCurrency(item.total)}</span>
                    </div>
                  ))}
                </div>
                
                {/* Actions */}
                <div className="space-y-2">
                  {selectedShow && onUpdateShow ? (
                    <button
                      onClick={handleSaveSession}
                      className="w-full py-3 bg-green-600 hover:bg-green-500 rounded-xl font-bold"
                    >
                      Save to Show & Exit
                    </button>
                  ) : (
                    <p className="text-center text-gray-500 text-sm py-2">
                      No show selected - sales tracked locally only
                    </p>
                  )}
                  <button
                    onClick={handleDiscardSession}
                    className="w-full py-3 bg-gray-700 hover:bg-gray-600 rounded-xl font-medium"
                  >
                    {selectedShow ? 'Discard & Exit' : 'Exit Sales Mode'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==================== SHOW PICKER MODAL ====================
  if (showPicker) {
    return (
      <div className="fixed inset-0 top-16 md:top-0 bg-gray-900 text-white flex flex-col z-50">
        <div className="flex-shrink-0 bg-gradient-to-r from-purple-600 to-pink-600 p-6">
          <h2 className="text-2xl font-bold text-center">Select a Show</h2>
          <p className="text-center text-white/80 mt-1">Link sales to a show for reporting</p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {availableShows.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CalendarIcon size={48} className="mx-auto mb-4 opacity-50" />
              <p>No upcoming shows found</p>
            </div>
          ) : (
            <div className="space-y-3 max-w-2xl mx-auto">
              {availableShows.map(show => (
                <button
                  key={`${show.dateKey}-${show.id}`}
                  onClick={() => handleSelectShow(show)}
                  className={`w-full p-4 rounded-xl text-left transition-all ${
                    show.isToday
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 shadow-lg'
                      : 'bg-gray-800 hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-lg">{show.title || 'Show'}</span>
                        {show.isToday && (
                          <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-bold">
                            TODAY
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm mt-1">
                        📍 {show.venue}{show.city ? `, ${show.city}` : ''}
                      </p>
                      <p className="text-gray-500 text-sm">
                        📅 {new Date(show.date).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                        {show.showTime && ` • 🕐 ${show.showTime}`}
                      </p>
                    </div>
                    <ChevronLeft size={24} className="rotate-180 text-gray-500" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex-shrink-0 p-4 border-t border-gray-800 pb-8">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={handleSkipShowSelection}
              className="w-full py-3 bg-gray-800 hover:bg-gray-700 rounded-xl font-medium"
            >
              Continue without selecting a show
            </button>
            <button
              onClick={() => setShowPicker(false)}
              className="w-full py-3 mt-2 text-gray-500 hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== NORMAL MODE UI ====================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 pb-20 md:pb-0">
      {/* Mobile Sticky Header */}
      <div className="sticky top-0 z-40 bg-white dark:bg-gray-800 shadow-md md:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => onNavigate('dashboard')}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">Inventory</h1>
          <div className="flex gap-1">
            <button onClick={() => setShowCurrencyPicker(true)} className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
              <Globe size={18} />
            </button>
            <ExportGate>
              <button onClick={exportToCSV} className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                <Download size={18} />
              </button>
            </ExportGate>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-2 md:p-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 md:p-6 border border-gray-100 dark:border-gray-700">
          
          {/* Desktop Header */}
          <div className="hidden md:flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-3">
                <Package className="text-blue-600" />
                Merch Inventory
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {inventoryStats.uniqueProducts.size} products • {inventoryStats.totalItems} units • {currency}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowCurrencyPicker(true)} className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm">
                <Globe size={16} />
                {currency}
              </button>
              <ExportGate>
                <button onClick={exportToCSV} className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm">
                  <Download size={16} />
                  Export
                </button>
              </ExportGate>
            </div>
          </div>

          {/* SALES MODE BUTTON - Prominent */}
          <SalesModeGate>
            <button
              onClick={handleEnterSalesMode}
              className="w-full mb-6 py-4 px-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-3 hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl transition-all"
            >
              <Zap size={24} className="flex-shrink-0" />
              <span>Enter Sales Mode</span>
              <span className="text-sm opacity-80 hidden sm:inline">Quick selling at merch table</span>
            </button>
          </SalesModeGate>

          {/* Stock Alerts */}
          {(inventoryStats.lowStock.length > 0 || inventoryStats.outOfStock.length > 0) && (
            <div className="mb-6 space-y-3">
              {inventoryStats.outOfStock.length > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-3">
                  <div className="flex items-start gap-3">
                    <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    <div className="min-w-0">
                      <h3 className="font-semibold text-red-800 dark:text-red-300 text-sm">Out of Stock</h3>
                      <p className="text-xs text-red-600 dark:text-red-400 truncate">{inventoryStats.outOfStock.map(i => getItemDisplayName(i)).join(', ')}</p>
                    </div>
                  </div>
                </div>
              )}
              {inventoryStats.lowStock.length > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-xl p-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                    <div className="min-w-0">
                      <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 text-sm">Low Stock</h3>
                      <p className="text-xs text-yellow-600 dark:text-yellow-400 truncate">{inventoryStats.lowStock.map(i => getItemDisplayName(i)).join(', ')}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* INVENTORY VALUE STATS */}
          <button 
            onClick={() => setShowStats(!showStats)} 
            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl mb-2"
          >
            <div className="flex items-center gap-3">
              <BarChart3 size={24} />
              <div className="text-left">
                <h3 className="font-bold">{formatCurrency(inventoryStats.totalValue)} retail value</h3>
                <p className="text-sm opacity-90">{inventoryStats.totalItems} units • {inventoryStats.uniqueProducts.size} products</p>
              </div>
            </div>
            {showStats ? <ChevronUp /> : <ChevronDown />}
          </button>

          {/* EXPANDED STATS PANEL */}
          {showStats && (
            <div className="mb-6 bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 space-y-4">
              {/* Cost vs Sale Price Overview */}
              <div>
                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <DollarSign size={18} className="text-green-600" />
                  Cost vs Retail Value
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Total Cost</p>
                    <p className="text-lg font-bold text-red-600 dark:text-red-400">{formatCurrency(inventoryStats.totalCost)}</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Retail Value</p>
                    <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{formatCurrency(inventoryStats.totalValue)}</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Potential Profit</p>
                    <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(inventoryStats.totalProfit)}</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Profit Margin</p>
                    <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{inventoryStats.profitMargin}%</p>
                  </div>
                </div>
              </div>

              {/* Breakdown by Category */}
              {Object.keys(inventoryStats.byCategory).length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                    <PieChart size={18} className="text-blue-600" />
                    By Category
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(inventoryStats.byCategory)
                      .sort((a, b) => b[1].totalValue - a[1].totalValue)
                      .map(([category, stats]) => (
                        <div key={category} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-gray-800 dark:text-gray-200 text-sm">{category}</span>
                            <span className="text-xs text-gray-500">{stats.totalItems} units</span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-500">
                              Cost: <span className="text-red-600">{formatCurrency(stats.totalCost)}</span>
                            </span>
                            <span className="text-gray-500">
                              Value: <span className="text-blue-600">{formatCurrency(stats.totalValue)}</span>
                            </span>
                            <span className="text-green-600 font-medium">
                              +{formatCurrency(stats.profit)}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Stock Status Summary */}
              <div>
                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Package size={18} className="text-orange-600" />
                  Stock Status
                </h4>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2 text-center border border-green-200 dark:border-green-800">
                    <CheckCircle2 className="mx-auto mb-1 text-green-600" size={20} />
                    <p className="text-xl font-bold text-green-700 dark:text-green-400">{inventoryStats.inStock.length}</p>
                    <p className="text-xs text-green-600">In Stock</p>
                  </div>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-2 text-center border border-yellow-200 dark:border-yellow-800">
                    <AlertTriangle className="mx-auto mb-1 text-yellow-600" size={20} />
                    <p className="text-xl font-bold text-yellow-700 dark:text-yellow-400">{inventoryStats.lowStock.length}</p>
                    <p className="text-xs text-yellow-600">Low Stock</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2 text-center border border-red-200 dark:border-red-800">
                    <XCircle className="mx-auto mb-1 text-red-600" size={20} />
                    <p className="text-xl font-bold text-red-700 dark:text-red-400">{inventoryStats.outOfStock.length}</p>
                    <p className="text-xs text-red-600">Out</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SHOW SALES HISTORY SECTION */}
          {showsWithMerchSales.length > 0 && (
            <>
              <button 
                onClick={() => setShowSalesHistoryPanel(!showSalesHistoryPanel)} 
                className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl mb-2"
              >
                <div className="flex items-center gap-3">
                  <Receipt size={24} />
                  <div className="text-left">
                    <h3 className="font-bold">Show Sales History</h3>
                    <p className="text-sm opacity-90">{showsWithMerchSales.length} shows with merch sales</p>
                  </div>
                </div>
                {showSalesHistoryPanel ? <ChevronUp /> : <ChevronDown />}
              </button>

              {showSalesHistoryPanel && (
                <div className="mb-6 bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <div className="space-y-4">
                    {showsWithMerchSales.map((show) => (
                      <div key={show.id} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-gray-800 dark:text-gray-100 truncate">{show.title || show.venue || 'Show'}</h4>
                            <p className="text-xs text-gray-500">
                              {show.venue && `📍 ${show.venue} • `}
                              📅 {new Date(show.dateKey).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0 ml-2">
                            <p className="text-lg font-bold text-green-600">{formatCurrency(show.totalRevenue)}</p>
                            <p className="text-xs text-gray-500">{show.totalItems} items</p>
                          </div>
                        </div>
                        
                        {show.merchSales && show.merchSales.length > 0 && (
                          <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Items Sold</p>
                            <div className="space-y-1">
                              {Object.entries(
                                show.merchSales.reduce((acc, sale) => {
                                  const key = `${sale.itemName}-${sale.size || 'N/A'}`;
                                  if (!acc[key]) {
                                    acc[key] = { ...sale, quantity: 0, total: 0 };
                                  }
                                  acc[key].quantity += sale.quantity || 1;
                                  acc[key].total += sale.total || (sale.quantity * sale.price) || 0;
                                  return acc;
                                }, {})
                              ).map(([key, item]) => (
                                <div key={key} className="flex justify-between text-sm">
                                  <span className="text-gray-600 dark:text-gray-400 truncate flex-1 min-w-0">
                                    {item.itemName} {item.size && item.size !== 'N/A' && `(${item.size})`}
                                    <span className="text-gray-400 ml-1">×{item.quantity}</span>
                                  </span>
                                  <span className="text-gray-800 dark:text-gray-200 ml-2 flex-shrink-0">{formatCurrency(item.total)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <button
                          onClick={() => handleClearHistoryClick(show)}
                          className="mt-3 flex items-center justify-center gap-2 w-full py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg transition-colors"
                        >
                          <Trash2 size={14} />
                          Clear Itemized History
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Search & Filters */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-3 mb-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-800 rounded-lg text-sm" />
              </div>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-800 rounded-lg text-sm">
                <option value="all">All</option>
                <option value="in">✅ In Stock</option>
                <option value="low">⚠️ Low</option>
                <option value="out">🚨 Out</option>
              </select>
            </div>
          </div>

          {/* Add Form Toggle */}
          <AddInventoryGate currentCount={inventory.length}>
            <button onClick={() => { setShowAddForm(!showAddForm); setEditingItem(null); }} className={`w-full mb-4 py-3 rounded-xl font-semibold ${showAddForm ? 'bg-gray-200 dark:bg-gray-700' : 'bg-blue-600 text-white'}`}>
              {showAddForm ? <><ChevronUp className="inline mr-2" /> Close</> : <><Plus className="inline mr-2" /> Add Item</>}
            </button>
          </AddInventoryGate>

          {showAddForm && (
            <div className="mb-6">
              <InventoryForm
                ref={formRef}
                onSubmit={editingItem ? handleUpdateItem : handleAddItem}
                initialValues={editingItem}
                isEditing={!!editingItem}
                onCancel={handleCancelEdit}
                itemTypes={itemTypes}
                sizes={sizes}
                currency={currency}
              />
            </div>
          )}

          {/* Inventory List */}
          <div className="space-y-4">
            {inventoryByType.length === 0 ? (
              <EmptyState icon={Package} title="No Inventory" description="Add items to track your merch" action={() => setShowAddForm(true)} actionLabel="Add First Item" />
            ) : (
              inventoryByType.map(({ type, items, totalQuantity, totalValue }) => (
                <div key={type} className="bg-gray-50 dark:bg-gray-900 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
                  <div className="mb-3">
                    <h3 className="text-base font-bold text-gray-800 dark:text-gray-100">{type}</h3>
                    <p className="text-xs text-gray-500">{items.length} items • {totalQuantity} units • {formatCurrency(totalValue)}</p>
                  </div>
                  
                  <div className="space-y-2">
                    {items.map(item => {
                      const displayName = getItemDisplayName(item);
                      const status = getStockStatus(item);
                      const hasSizes = itemHasSizes(item);
                      const totalQty = getItemTotalQuantity(item);
                      const isExpanded = expandedItems.has(item.id);

                      return (
                        <div key={item.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                          <div className="p-3">
                            <div className="flex items-start gap-2">
                              {item.imageUrl ? (
                                <img src={item.imageUrl} alt={displayName} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                  <Package size={20} className="text-gray-400" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0 flex-1">
                                    <h4 className="font-bold text-gray-800 dark:text-gray-100 text-sm truncate">{displayName}</h4>
                                    {item.name && <p className="text-xs text-gray-500 truncate">{item.item}</p>}
                                  </div>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 flex-shrink-0 ${status.bgClass}`}>
                                    {status.icon}
                                    {status.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-sm">
                                  <span className="font-semibold text-gray-800 dark:text-gray-100">{totalQty}</span>
                                  <span className="font-semibold text-green-600">{formatCurrency(item.sellPrice)}</span>
                                </div>
                              </div>
                            </div>

                            {/* Action buttons row */}
                            <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                              <div className="flex items-center gap-1">
                                <button onClick={() => handleViewItem(item)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" title="View">
                                  <Eye size={16} className="text-gray-500" />
                                </button>
                                <button onClick={() => handleEditItem(item)} className="p-1.5 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg" title="Edit">
                                  <Edit2 size={16} className="text-blue-600" />
                                </button>
                                <button onClick={() => setConfirmDelete(item)} className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg" title="Delete">
                                  <X size={16} className="text-red-600" />
                                </button>
                              </div>
                              
                              {hasSizes ? (
                                <button
                                  onClick={() => toggleItemExpanded(item.id)}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm font-medium"
                                >
                                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                  Sizes
                                </button>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <button 
                                    onClick={() => handleQuickQuantityChange(item, Math.max(0, parseInt(item.quantity) - 1))} 
                                    className="p-1.5 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-lg"
                                  >
                                    <Minus size={14} />
                                  </button>
                                  <span className="font-bold min-w-[1.5rem] text-center text-sm">{item.quantity}</span>
                                  <button 
                                    onClick={() => handleQuickQuantityChange(item, parseInt(item.quantity) + 1)} 
                                    className="p-1.5 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-lg"
                                  >
                                    <Plus size={14} />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {hasSizes && isExpanded && (
                            <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3">
                              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                                {getItemSizes(item).map((sizeData) => {
                                  const qty = parseInt(sizeData.quantity) || 0;
                                  const isOut = qty === 0;
                                  const isLow = qty > 0 && qty <= parseInt(item.reorderPoint || 5);
                                  return (
                                    <div key={sizeData.size} className={`p-2 rounded-lg border text-center ${
                                      isOut ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 
                                      isLow ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' :
                                      'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                                    }`}>
                                      <span className="font-bold block text-sm">{sizeData.size}</span>
                                      <div className="flex items-center justify-center gap-1 my-1">
                                        <button onClick={() => handleSizeQuantityChange(item, sizeData.size, Math.max(0, qty - 1))} className="p-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 rounded">
                                          <Minus size={10} />
                                        </button>
                                        <span className="font-bold min-w-[1.2rem] text-xs">{qty}</span>
                                        <button onClick={() => handleSizeQuantityChange(item, sizeData.size, qty + 1)} className="p-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 rounded">
                                          <Plus size={10} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <ConfirmModal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={() => handleDeleteItem(confirmDelete)} title="Delete Item?" message={`Delete ${confirmDelete ? getItemDisplayName(confirmDelete) : ''}?`} danger />
      <InventoryDetailModal isOpen={!!viewingItem} onClose={() => setViewingItem(null)} item={viewingItem} onEdit={handleEditItem} onDelete={(item) => setConfirmDelete(item)} currency={currency} />

      {/* Clear History Confirmation Modal */}
      {clearHistoryModal.isOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="bg-orange-50 dark:bg-orange-900/30 p-6 border-b border-orange-100 dark:border-orange-800">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center">
                  <Trash2 className="text-orange-600" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Clear Itemized List</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {clearHistoryModal.show?.title || clearHistoryModal.show?.venue || 'This show'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                This will hide the itemized breakdown (which items sold) from the show details. 
              </p>
              
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-4">
                <p className="text-sm text-green-800 dark:text-green-300 flex items-start gap-2">
                  <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Your merch total stays the same!</strong> The 
                    <span className="font-bold"> {formatCurrency(clearHistoryModal.show?.totalRevenue || 0)} </span> 
                    will still show on the show and in your reports.
                  </span>
                </p>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-900 p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col gap-3">
                <div className="flex gap-3">
                  <button
                    onClick={() => setClearHistoryModal({ isOpen: false, show: null })}
                    className="flex-1 py-3 px-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-xl font-medium transition-colors"
                  >
                    Keep List
                  </button>
                  <button
                    onClick={() => handleConfirmClearHistory(false)}
                    className="flex-1 py-3 px-4 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-medium transition-colors"
                  >
                    Clear List
                  </button>
                </div>
                <button
                  onClick={() => handleConfirmClearHistory(true)}
                  className="w-full py-2.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  Clear & don't ask me again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Currency Picker Modal */}
      {showCurrencyPicker && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-xl font-bold">Select Currency</h3>
              <button onClick={() => setShowCurrencyPicker(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"><X size={24} /></button>
            </div>
            <div className="p-2 max-h-[60vh] overflow-y-auto">
              {Object.entries(CURRENCIES).map(([code, { symbol, name }]) => (
                <button key={code} onClick={() => handleCurrencyChange(code)} className={`w-full p-4 text-left rounded-xl mb-1 flex items-center justify-between ${currency === code ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                  <span><span className="font-bold text-lg mr-2">{symbol}</span>{name}</span>
                  <span className="font-mono opacity-60">{code}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sales Mode Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="salesMode"
        title="Sales Mode"
        description="Track merch sales in real-time at your shows"
      />
    </div>
  );
};

export default Inventory;